import { ApiResponse } from '../../../../../shared/infrastructure/types/api/api-types';
import {
  CheckPhoneCodeResponse,
  CreateWithAccountHeaders,
  ExecuteAuthCallbackRequest,
  ExecuteAuthCallbackResponse,
  LoginRequest,
  LoginResponse,
  RecoveryErrorData,
  RecoveryRequestRequest,
  RecoveryResetErrorData,
  RecoveryResetRequest,
  RecoveryResetValidateResponse,
  InvitationValidateResponse,
  InvitationConfirmRequest,
  InvitationErrorData,
  RegisterErrorData,
  RegisterRequest,
  RegisterAccountResponse,
  RequestPhoneVerificationParams,
  RequestPhoneVerificationResponse,
  PhoneVerificationKind,
  PhoneVerificationRetry,
  PhoneVerificationErrorData,
  TemporarySignInRequest,
  UsersMeResponse,
  ProductResponse,
  ShardResponse,
  RefreshTokenResponse,
  UserInfo,
  ManifestData,
  RequestVerificationPrepareResponse,
  CheckCodeByIDResponse,
} from './auth-contracts';
import {
  LoadingStatus,
  SubmittingStatus,
} from '../../../../../shared/infrastructure/types/common/common-types';

declare global {
  interface Window {
    smartCaptcha?: {
      render: (containerId: string, options: { sitekey: string; hl?: string }) => string;
      subscribe: (widgetId: string, event: string, callback: (token?: string) => void) => void;
      destroy?: (widgetId?: string) => void;
    };
  }
}

enum IncomingPhone {
  TEST = '+79326985664',
  PROD = '+79863138126',
}

enum AuthTab {
  REGISTER = 'register',
  LOGIN = 'login',
}

enum LoginValidationError {
  EMAIL_EMPTY = 'login.validation.email.empty',
  EMAIL_INCORRECT = 'login.validation.email.incorrect',
  PASSWORD_EMPTY = 'login.validation.password.empty',
}

enum RecoveryValidationError {
  EMAIL_EMPTY = 'recovery.validation.email.empty',
  EMAIL_INCORRECT = 'recovery.validation.email.incorrect',
  EMAIL_NOT_FOUND = 'recovery.error.emailNotFount',
  PASSWORD_EMPTY = 'recovery.validation.password.empty',
  PASSWORD_INCORRECT = 'recovery.validation.password.incorrect',
  PASSWORD_CONFIRM_MISMATCH = 'recovery.validation.passwordConfirm.mismatch',
}

enum RegisterValidationError {
  PHONE_EMPTY = 'register.validation.phone.empty',
  PHONE_INCORRECT = 'register.validation.phone.incorrect',
  FIO_EMPTY = 'register.validation.fio.empty',
  EMAIL_EMPTY = 'register.validation.email.empty',
  EMAIL_INCORRECT = 'register.validation.email.incorrect',
  PASSWORD_EMPTY = 'register.validation.password.empty',
  PASSWORD_INCORRECT = 'register.validation.password.incorrect',
  PROMOCODE_INVALID = 'register.validation.promocode.incorrect',
  CAPTCHA_NOT_ENDED = 'register.validation.captcha.not.ended',
  PHONE_CODE_INVALID = 'register.validation.phone.code.invalid',
}

enum RegisterStep {
  ENTER_PHONE = 'enterPhone',
  CONFIRM_PHONE = 'confirmPhone',
  CHANGE_PHONE = 'changePhone',
  FORM = 'form',
}

enum RestoreStep {
  ENTER_EMAIL = 'enterEmail',
  WAIT_EMAIL = 'waitEmail',
  NEW_PASSWORD = 'newPassword',
}

type ConfirmationMethod = 'outgoing' | 'incoming' | 'qr';

const CONFIRMATION_DEFAULT_EXPIRES_IN = 299;
const CONFIRMATION_PLACEHOLDER = '—';
const PHONE_CODE_LENGTH = 4;
const UNEXPECTED_ERROR_KEY = 'common.unexpectedError';
const COMPACT_PRODUCT_CODE = 'beeline';
const YANDEX_CLOUD_PROVIDER = 'yc';
const YANDEX_CLOUD_STORAGE_KEY = 'transferable_query_tokens';

type YandexCloudError =
  | 'expired_token'
  | 'invalid_token'
  | 'missing_token'
  | 'missing_expiration'
  | 'missing_product_instance_id'
  | 'not_yandex_cloud_account'
  | 'already_claimed'
  | 'license_lock_missing'
  | 'claim_rejected'
  | 'request_failed'
  | 'authorization_failed'
  | 'operation_in_progress'
  | 'invalid_response'
  | 'resource_id_mismatch'
  | 'resource_id_missing'
  | 'unsupported_license_template';

const YANDEX_CLOUD_ERROR_KEY: { [K in YandexCloudError]: string } = {
  expired_token: 'yandexCloud.error.expiredToken',
  invalid_token: 'yandexCloud.error.invalidToken',
  missing_token: 'yandexCloud.error.invalidToken',
  missing_expiration: 'yandexCloud.error.invalidToken',
  missing_product_instance_id: 'yandexCloud.error.invalidToken',
  not_yandex_cloud_account: 'yandexCloud.error.notYandexCloudAccount',
  already_claimed: 'yandexCloud.error.alreadyClaimed',
  license_lock_missing: 'yandexCloud.error.licenseBindingFailed',
  claim_rejected: 'yandexCloud.error.licenseBindingFailed',
  request_failed: 'yandexCloud.error.licenseBindingFailed',
  authorization_failed: 'yandexCloud.error.licenseBindingFailed',
  operation_in_progress: 'yandexCloud.error.licenseBindingFailed',
  invalid_response: 'yandexCloud.error.licenseBindingFailed',
  resource_id_mismatch: 'yandexCloud.error.licenseBindingFailed',
  resource_id_missing: 'yandexCloud.error.licenseBindingFailed',
  unsupported_license_template: 'yandexCloud.error.licenseBindingFailed',
};

const YANDEX_CLOUD_BLOCKING_ERRORS: YandexCloudError[] = [
  'expired_token',
  'invalid_token',
  'missing_token',
  'missing_expiration',
  'missing_product_instance_id',
];

interface YandexCloudTransition {
  isActive: boolean;
  isFromURL: boolean;
  token?: string;
  authError?: string;
}

type TextLogoProductCode = 'trainer' | 'sara';

const PRODUCT_TEXT_LOGO: { [K in TextLogoProductCode]: string } = {
  trainer: 'Скорозвон Ai-тренер',
  sara: 'Скорозвон Речевая аналитика',
};

type PhoneVerificationError =
  | 'invalid_phone'
  | 'inadmissable_phone'
  | 'register_server_error'
  | 'captcha_invalid'
  | 'captcha_error'
  | 'daily_limit_exceed';

const PHONE_VERIFICATION_ERROR_KEY: { [K in PhoneVerificationError]: string } = {
  invalid_phone: 'register.error.invalidPhone',
  inadmissable_phone: 'register.error.inadmissablePhone',
  register_server_error: 'register.error.serverError',
  captcha_invalid: 'register.error.captchaFailed',
  captcha_error: 'register.error.captchaFailed',
  daily_limit_exceed: 'register.error.dailyLimitExceed',
};

const CONFIRMATION_METHOD_KIND: { [K in ConfirmationMethod]: PhoneVerificationKind } = {
  outgoing: 'callback',
  incoming: 'incoming',
  qr: 'qr',
};

enum TrackingCookie {
  TRACKING_URL = 'tracking_url',
  LATEST_REQUEST_URI = '_latest_request_uri',
  PARTNER_REFERRER = '_partner_referrer',
  TRACKING_REFERRER = 'tracking_referrer',
  REFERRER = '_referrer',
  ROISTAT_VISIT = 'roistat_visit',
  YM_UID = '_ym_uid',
  GA_UID = '_ga',
  REGISTRATION_START_PAGE = 'skz_registration_start_page',
}

const TRACKING_COOKIES_TO_CLEAR = [
  TrackingCookie.TRACKING_URL,
  TrackingCookie.LATEST_REQUEST_URI,
  TrackingCookie.PARTNER_REFERRER,
  TrackingCookie.TRACKING_REFERRER,
  TrackingCookie.REFERRER,
] as const;

enum RegistrationGoal {
  PHONE_ENTER = 'regnomer',
  PHONE_SUBMIT = 'regcod',
  PROFILE_SUBMIT = '3Registerfull',
  SERVICE_LOGIN = 'reg_service_login',
}

const REGISTRATION_START_PAGE_FALLBACK = '_start_page_not_found' as MetrikaStartPage;
const METRIKA_PRODUCT_CODE = 'skorozvon' as ProductCode;

interface AuthorizationStatuses {
  loginForm: SubmittingStatus;
  loginLoadingStatus: LoadingStatus;
  verificationPrepare: LoadingStatus;
  registerFormStatus: SubmittingStatus;
  recoveryFormStatus: SubmittingStatus;
  enterPhoneLoading: LoadingStatus;
}

interface IAuthorizationService {
  setupAuthorization: (accountID: AccountID) => void;
  clearAuthorization: () => void;
  setAuthToken: (token: AccessTokenBody) => void;
  setAppServers: (servers: Url[]) => void;
  setLocales: (locales: string[]) => void;
  setRejectReason: (reason: string) => void;
  getRejectReason: () => string | null;
  saveHashRoute: () => void;
  applyCrossDomainCookieFromQuery: () => void;
  saveUserInfo: (data: UserInfo) => void;
  loadUserInfo: () => UserInfo | null;
  getAccessToken: () => string | null;
  getManifestPath: () => string;
  clearURLAccessToken: () => void;
  consumeYandexCloudTransition: () => YandexCloudTransition;
  saveYandexCloudTransition: (token: string) => void;
  clearYandexCloudTransition: () => void;
  startAuthCheckInterval: (callback: () => void) => void;
  startTokenRefreshInterval: (expiresIn: number, callback: () => void) => void;
  startTokenRefreshTimeout: (callback: () => void) => void;
  clearTimeouts: () => void;
}

interface IAuthDataSource {
  logout: () => Promise<ApiResponse<unknown>>;
  login: (data: LoginRequest) => Promise<ApiResponse<LoginResponse>>;
  executeAuthCallback: (
    data: ExecuteAuthCallbackRequest,
  ) => Promise<ApiResponse<ExecuteAuthCallbackResponse>>;
  getUsersMe: (params?: { access_token?: string | null }) => Promise<ApiResponse<UsersMeResponse>>;
  getProduct: (host: string) => Promise<ApiResponse<ProductResponse>>;
  temporarySignIn: (data: TemporarySignInRequest) => Promise<ApiResponse<LoginResponse>>;
  getShard: (accountId: AccountID) => Promise<ApiResponse<ShardResponse>>;
  refreshToken: () => Promise<ApiResponse<RefreshTokenResponse>>;
  getManifest: (url: Url) => Promise<ApiResponse<ManifestData>>;
  requestPhoneVerification: (
    params: Omit<RequestPhoneVerificationParams, 'product'>,
    retry?: PhoneVerificationRetry,
  ) => Promise<ApiResponse<RequestPhoneVerificationResponse, PhoneVerificationErrorData>>;
  requestVerificationPrepare: (
    params: RequestPhoneVerificationParams,
  ) => Promise<ApiResponse<RequestVerificationPrepareResponse, PhoneVerificationErrorData>>;
  checkCodeByID: (
    id: string,
    token: string,
  ) => Promise<ApiResponse<CheckCodeByIDResponse, unknown>>;
  checkPhoneCode: (
    registrationId: string,
    code: PhoneCode,
    accessToken?: string,
  ) => Promise<ApiResponse<CheckPhoneCodeResponse, PhoneVerificationErrorData>>;
  register: (
    data: RegisterRequest,
  ) => Promise<ApiResponse<RegisterAccountResponse, RegisterErrorData>>;
  createWithAccount: (
    path: string,
    data: unknown,
    headers: CreateWithAccountHeaders,
  ) => Promise<ApiResponse<unknown, RegisterErrorData>>;
  requestRecoveryEmail: (
    data: RecoveryRequestRequest,
  ) => Promise<ApiResponse<unknown, RecoveryErrorData>>;
  resetPassword: (
    data: RecoveryResetRequest,
  ) => Promise<ApiResponse<unknown, RecoveryResetErrorData>>;
  validateResetPasswordToken: (
    token: string,
  ) => Promise<ApiResponse<RecoveryResetValidateResponse, RecoveryResetErrorData>>;
  validateInvitation: (
    token: string,
  ) => Promise<ApiResponse<InvitationValidateResponse, InvitationErrorData>>;
  confirmInvitation: (
    token: string,
    data: InvitationConfirmRequest,
  ) => Promise<ApiResponse<unknown, InvitationErrorData>>;
}

export {
  type IAuthorizationService,
  type IAuthDataSource,
  type AuthorizationStatuses,
  AuthTab,
  LoginValidationError,
  RecoveryValidationError,
  RegisterValidationError,
  RegisterStep,
  RestoreStep,
  type ConfirmationMethod,
  type PhoneVerificationError,
  CONFIRMATION_DEFAULT_EXPIRES_IN,
  CONFIRMATION_PLACEHOLDER,
  CONFIRMATION_METHOD_KIND,
  PHONE_CODE_LENGTH,
  PHONE_VERIFICATION_ERROR_KEY,
  UNEXPECTED_ERROR_KEY,
  COMPACT_PRODUCT_CODE,
  type TextLogoProductCode,
  PRODUCT_TEXT_LOGO,
  type YandexCloudError,
  type YandexCloudTransition,
  YANDEX_CLOUD_ERROR_KEY,
  YANDEX_CLOUD_BLOCKING_ERRORS,
  YANDEX_CLOUD_PROVIDER,
  YANDEX_CLOUD_STORAGE_KEY,
  IncomingPhone,
  TrackingCookie,
  TRACKING_COOKIES_TO_CLEAR,
  RegistrationGoal,
  REGISTRATION_START_PAGE_FALLBACK,
  METRIKA_PRODUCT_CODE,
};
